import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ChatMessage, ChatSession, DeletedArtifactRef } from '../../../models/chat.models';
import { ConversationStreamEvent, DeoConversation } from '../../../models/deo-conversation.model';
import { isTempSessionId, keepInFlightChats } from '../../../models/temp-session.constants';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { AuthService } from 'app/services/auth/auth.service';
import { AIAgentService } from '../../../services/ai-agent.service';
import { DeoOwnershipService } from '../../../services/deo-ownership.service';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { DeoConversationService } from '../../../services/deo-conversation.service';
import { DEOAiAssistantService } from '../../../services/deo-ai-assistant.service';
import { AlertAskDeoContext } from 'app/shared/models/alert-ask-deo-context.model';
import { formatRelativeTimeLabel } from 'app/shared/util/relative-time.util';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_DEO_AGENT_PROJECT_INSTRUCTIONS } from 'app/shared/services/feature-flags/feature-flags.constants';

// Shown instead of a relative label when there is no project in view, or the backend sent no
// usable updated_at; matches the projects grid so the same project reads the same on both.
const UNKNOWN_UPDATED_LABEL = '--';

/** Where an anomaly is answered: the assistant screen, not a project folder. */
const DEO_ASSISTANT_ROUTE = '/deo-ai-assist';

@Component({
  standalone: false,
  selector: 'deo-project-folder-view',
  templateUrl: './deo-project-folder-view.component.html',
  styleUrls: ['./deo-project-folder-view.component.scss']
})
export class DeoProjectFolderViewComponent implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  isSidebarCollapsed = false;
  currentMessage = '';
  /** The conversation currently shown; its live messages survive navigation (DeoConversationService). */
  currentConversation: DeoConversation | null = null;
  chatSessions: ChatSession[] = [];
  currentSessionId: string | null = null;
  activeTab: 'instructions' | 'chats' | 'artifacts' | 'welcome' | 'one-chat' = 'welcome';

  // File attachment
  selectedFile: File | null = null;
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;

  // UI state
  isListening = false;
  voiceError: string | null = null;
  userName = 'User';
  isRightPanelLoading = false;
  isProjectInstructionsEnabled = false;

  // Project state
  projectId = '';
  projectName = '';
  projectChatsCount = 0;
  projectArtifactsCount = 0;
  projectUpdatedAt: Date | null = null;
  /** False until the project list confirms the user owns this project, so a viewer never gets edit controls while it loads or after it fails. */
  isProjectOwner = false;

  get starredSessions(): ChatSession[] {
    return this.chatSessions.filter(session => session.isFavorite);
  }

  get recentSessions(): ChatSession[] {
    return this.chatSessions.filter(session => !session.isFavorite);
  }

  isCurrentChatFavorite(): boolean {
    return this.chatSessions.find(s => s.id === this.currentSessionId)?.isFavorite ?? false;
  }

  /** Keeps the sidebar's starred/recent split in sync when the open chat toggles its own favorite state (already persisted by the child). */
  onCurrentChatFavoriteChanged(isFavorite: boolean): void {
    const session = this.chatSessions.find(s => s.id === this.currentSessionId);
    if (session) {
      session.isFavorite = isFavorite;
    }
  }

  get currentChatTitle(): string {
    return this.currentSession?.title ?? 'New Chat';
  }

  get isCurrentChatReadOnly(): boolean {
    return !this._ownershipService.isMine(this.currentSession?.user_id);
  }

  get currentChatOwner(): string {
    return this.currentSession?.user_id ?? '';
  }

  private get currentSession(): ChatSession | undefined {
    return this.chatSessions.find(s => s.id === this.currentSessionId);
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _authService: AuthService,
    private readonly _aiAgentService: AIAgentService,
    private readonly _conversationService: DeoConversationService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _deoAiAssistantService: DEOAiAssistantService,
    private readonly _ownershipService: DeoOwnershipService,
    private readonly _gainsToastService: GainsToastService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  /** Messages render from the live conversation; the streaming engine mutates this array in place. */
  get messages(): ChatMessage[] {
    return this.currentConversation?.messages ?? [];
  }

  /** True while this conversation is producing a response (the engine lives in DeoConversationService). */
  get isStreaming(): boolean {
    return this._conversationService.isStreaming(this.currentConversation);
  }

  /** Retained for the welcome-screen disabled states; streaming covers the whole send-to-finish window. */
  get isLoading(): boolean {
    return this.isStreaming;
  }

  /** Retained for the welcome-screen disabled states; mirrors the streaming flag. */
  get isTyping(): boolean {
    return this.isStreaming;
  }

  ngOnInit(): void {
    this.userName = this._authService.getGivenName() || 'User';
    this.isProjectInstructionsEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_PROJECT_INSTRUCTIONS);

    this._conversationService.streamEvents$.pipe(takeUntil(this._destroy$)).subscribe(event => this.onConversationEvent(event));

    this._deoAiAssistantService.setPageChatMounted(true);
    this._deoAiAssistantService.alertContext$.pipe(takeUntil(this._destroy$)).subscribe(context => this.handOverAlertToScreen(context));

    this._activatedRoute.queryParams.pipe(takeUntil(this._destroy$)).subscribe(params => {
      this.projectId = params['projectId'] || '';

      if (this.projectId) {
        this.loadProjectSessions(this.projectId);
        this.loadProjectDetails(this.projectId);
      } else {
        this.resetProjectMeta();
      }

      if (this.messages.length === 0) {
        this.createNewChat();
      }
    });
  }

  ngOnDestroy(): void {
    this._deoAiAssistantService.setPageChatMounted(false);
    this._destroy$.next();
    this._destroy$.complete();
  }

  private loadProjectSessions(projectId: string): void {
    this._aiAgentService
      .getProjectSessions(projectId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          const sessions = Array.isArray(response?.data) ? response.data : [];
          this.setProjectSessions(sessions);
        },
        error: () => {
          this.setProjectSessions([]);
        }
      });
  }

  /**
   * Populate the header (name, artifact count, updated date) from the project list so it works on a
   * direct/deep-linked URL too. The chats count stays live from the loaded session list.
   */
  private loadProjectDetails(projectId: string): void {
    this.isProjectOwner = false;
    this._aiAgentService
      .getProjects()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          const project = response?.succeeded ? (response.data ?? []).find(p => String(p.id) === projectId) : undefined;
          if (!project) {
            return;
          }
          this.projectName = project.name ?? '';
          this.projectArtifactsCount = project.artifacts_count ?? 0;
          this.projectUpdatedAt = this.parseDate(project.updated_at);
          this.isProjectOwner = this._ownershipService.isMine(project.user_id);
          this._cdr.markForCheck();
        },
        error: () => {
          // Header metadata stays at its defaults; the session list still loads independently.
        }
      });
  }

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /** Replaces loaded sessions while keeping a chat whose first answer is still streaming at the top. */
  private setProjectSessions(sessions: ChatSession[]): void {
    this.chatSessions = keepInFlightChats(this.chatSessions, sessions);
    this.projectChatsCount = sessions.length;
    this._cdr.markForCheck();
  }

  private resetProjectMeta(): void {
    this.projectName = '';
    this.projectChatsCount = 0;
    this.projectArtifactsCount = 0;
    this.projectUpdatedAt = null;
    this.isProjectOwner = false;
  }

  /**
   * Reflects a backend `updated_at` bump in the header without a refetch. Creating a chat, generating an
   * artifact, or moving a chat out of the project all touch the project server-side; a refetch right after
   * can still read the pre-bump timestamp, so we optimistically show "just now" (consistent with how the
   * artifact counts are bumped). The list view reloads authoritatively the next time it is opened.
   */
  private markProjectUpdated(): void {
    if (!this.projectId) {
      return;
    }
    this.projectUpdatedAt = new Date();
    this._cdr.markForCheck();
  }

  get projectUpdatedLabel(): string {
    return formatRelativeTimeLabel(this.projectUpdatedAt) || UNKNOWN_UPDATED_LABEL;
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  changeActiveTab(tab: 'instructions' | 'chats' | 'artifacts' | 'welcome' | 'one-chat'): void {
    if (tab === 'instructions' && (!this.isProjectInstructionsEnabled || !this.projectId)) {
      return;
    }
    this.activeTab = tab;
  }

  /** Saving instructions touches the project, so the header's "Updated" label moves with it. */
  onInstructionsSaved(): void {
    this.markProjectUpdated();
  }

  createNewChat(): void {
    this.activeTab = 'welcome';
    this.startNewConversation();
    this.currentMessage = '';
  }

  /**
   * An anomaly is not project work, so this folder does not answer it: the question is kept for the
   * assistant screen and the reviewer is taken there.
   *
   * Filing it here would put the chat inside this project and hand it the project's instructions,
   * neither of which the reviewer asked for.
   */
  private handOverAlertToScreen(context: AlertAskDeoContext): void {
    this._deoAiAssistantService.deferAlertContext(context);
    this._router.navigate([DEO_ASSISTANT_ROUTE]);
  }

  /**
   * Opens a fresh conversation in the composer. No folder entry exists until the user submits a first
   * message, which DeoConversationService reports back as a `registered` event.
   */
  private startNewConversation(): void {
    const conversation = this._conversationService.reuseOrStartConversation(this.currentConversation);
    // Tag the conversation with this folder's project so the service links it on completion, even if the
    // user navigates out of the folder before the first response returns. A reused conversation needs it
    // just as much: the folder it was opened in may not be the folder it is sent from.
    conversation.projectId = this.projectId || null;
    this.currentConversation = conversation;
    this.currentSessionId = null;
  }

  selectChat(sessionId: string): void {
    this.activeTab = 'one-chat';
    this.currentSessionId = sessionId;
    // openSession hands back an in-flight or already-loaded conversation as-is, so a streaming response
    // keeps showing; otherwise it loads the history, which it skips for a chat with no real id yet.
    this.currentConversation = this._conversationService.openSession(sessionId);
  }

  onChatDeleted(sessionId: string): void {
    this.removeDeletedSessions([sessionId]);
  }

  onChatsDeleted(sessionIds: string[]): void {
    this.removeDeletedSessions(sessionIds);
  }

  /** A chat removed from this project (moved elsewhere or detached) no longer belongs in the folder list. */
  onChatRemovedFromProject(sessionId: string): void {
    this.removeDeletedSessions([sessionId]);
    // Reassigning a chat's project touches this project's updated_at server-side.
    this.markProjectUpdated();
  }

  private removeDeletedSessions(sessionIds: string[]): void {
    if (!sessionIds.length) {
      return;
    }

    this.chatSessions = this.chatSessions.filter(session => !sessionIds.includes(session.id));
    this.projectChatsCount = this.chatSessions.filter(session => !isTempSessionId(session.id)).length;

    if (this.currentSessionId && sessionIds.includes(this.currentSessionId)) {
      const nextSession = this.chatSessions[0];
      if (nextSession) {
        this.selectChat(nextSession.id);
      } else {
        this.createNewChat();
      }
    }

    this._cdr.markForCheck();
  }

  onWelcomeScreenSend(data: { messageContent: string; attachment?: { name: string; size: number; type: string } }): void {
    if (!this.currentConversation || this.currentConversation.status === 'streaming') {
      this.startNewConversation();
    }
    this.activeTab = 'one-chat';
    this.selectedFile = null;
    this._conversationService.send(this.currentConversation!, data.messageContent, data.attachment);
  }

  private onConversationEvent(event: ConversationStreamEvent): void {
    if (event.type === 'updated') {
      if (this.currentConversation?.clientId === event.clientId) {
        this._cdr.markForCheck();
      }
    } else if (event.type === 'registered') {
      this.handleConversationRegistered(event);
    } else if (event.type === 'completed') {
      this.handleConversationCompleted(event);
    } else if (event.type === 'error' && this.currentConversation?.clientId === event.clientId) {
      this._gainsToastService.error('Error', event.error);
    }
  }

  /**
   * Gives the chat the user just started its folder entry, titled with the message they sent.
   *
   * projectChatsCount is deliberately left alone: it counts the chats the project holds, and this one is
   * not one of them until the backend has created it.
   */
  private handleConversationRegistered(event: Extract<ConversationStreamEvent, { type: 'registered' }>): void {
    if (this.currentConversation?.clientId !== event.clientId) {
      return;
    }

    this.currentSessionId = event.sessionId;
    this.chatSessions.unshift({
      id: event.sessionId,
      title: event.title,
      created_at: new Date(),
      last_message_at: new Date(),
      user_id: this._authService.getEmail() ?? '',
      artifactCount: 0
    });
    this._cdr.markForCheck();
  }

  private handleConversationCompleted(event: Extract<ConversationStreamEvent, { type: 'completed' }>): void {
    if (!this.isEventForThisFolder(event)) {
      return;
    }

    if (event.isNew) {
      this.chatSessions = this.chatSessions.filter(session => session.id !== event.previousSessionId);
      // The artifact total starts at zero because bumpSessionArtifactCount owns the optimistic value for
      // this same completion — seeding it here too would count the first artifacts twice.
      this.chatSessions.unshift({
        id: event.sessionId,
        title: event.title,
        created_at: new Date(),
        last_message_at: new Date(),
        user_id: this._authService.getEmail() ?? '',
        artifactCount: 0
      });
      if (this.currentConversation?.clientId === event.clientId && this.currentSessionId === event.previousSessionId) {
        this.currentSessionId = event.sessionId;
      }
      // The project link itself is persisted by DeoConversationService (survives navigation); here we just
      // keep the folder's chat count in sync.
      this.projectChatsCount = this.chatSessions.filter(session => !isTempSessionId(session.id)).length;
    }

    if (event.artifactCount > 0) {
      // Optimistic only: the sessions endpoint would still report the pre-generation count until the
      // backend has indexed the new artifacts. It corrects itself on the next load of the folder.
      this.bumpSessionArtifactCount(event.sessionId, event.artifactCount);
      this.projectArtifactsCount += event.artifactCount;
    }

    // A new chat or a new artifact both bump the project's updated_at server-side.
    if (event.isNew || event.artifactCount > 0) {
      this.markProjectUpdated();
    }

    this._cdr.markForCheck();
  }

  /** Only react to completions for chats that belong to this folder (its temp/real session, or the open conversation). */
  private isEventForThisFolder(event: Extract<ConversationStreamEvent, { type: 'completed' }>): boolean {
    if (this.currentConversation?.clientId === event.clientId) {
      return true;
    }
    const trackedId = event.isNew ? event.previousSessionId : event.sessionId;
    return !!trackedId && this.chatSessions.some(session => session.id === trackedId);
  }

  /**
   * Keeps this folder's artifact totals truthful after the artifacts tab deletes something. The totals are
   * decremented rather than recounted because this screen holds no artifact list of its own — which is also
   * why the owning chat travels with each deleted id.
   */
  onArtifactsDeleted(deletedArtifacts: DeletedArtifactRef[]): void {
    if (!deletedArtifacts?.length) {
      return;
    }

    const deletedIds = new Set(deletedArtifacts.map(artifact => artifact.id));
    this.projectArtifactsCount = Math.max(0, this.projectArtifactsCount - deletedIds.size);
    this.decrementSessionArtifactCounts(deletedArtifacts);
    this._cdr.markForCheck();
  }

  /** chatSessions is reassigned rather than mutated so the chat list re-renders with the new totals. */
  private decrementSessionArtifactCounts(deletedArtifacts: DeletedArtifactRef[]): void {
    const deletionsPerSession = new Map<string, number>();

    deletedArtifacts.forEach(({ sessionId }) => {
      if (sessionId) {
        deletionsPerSession.set(sessionId, (deletionsPerSession.get(sessionId) ?? 0) + 1);
      }
    });

    this.chatSessions = this.chatSessions.map(session => {
      const deletedCount = deletionsPerSession.get(session.id);
      return deletedCount ? { ...session, artifactCount: Math.max(0, (session.artifactCount ?? 0) - deletedCount) } : session;
    });
  }

  /**
   * Optimistically bump a session's artifact total after a generation, since the sessions endpoint can
   * still report the pre-generation count until the backend has indexed the new artifacts.
   */
  private bumpSessionArtifactCount(sessionId: string, delta: number): void {
    if (delta <= 0) {
      return;
    }
    const session = this.chatSessions.find(chatSession => chatSession.id === sessionId);
    if (session) {
      session.artifactCount = (session.artifactCount ?? 0) + delta;
    }
    this._cdr.markForCheck();
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    this.selectedFile = file as unknown as File;
    return false;
  };

  onRightPanelLoadingChange(isLoading: boolean): void {
    if (!this.isDataTab(this.activeTab)) {
      return;
    }

    // Defer updates emitted during child init to avoid NG0100 in parent template checks.
    queueMicrotask(() => {
      if (!this.isDataTab(this.activeTab)) {
        return;
      }

      this.isRightPanelLoading = isLoading;
      this._cdr.markForCheck();
    });
  }

  private isDataTab(tab: any): boolean {
    return tab === 'chats' || tab === 'instructions' || tab === 'artifacts';
  }

  shouldShowRightPanelSpinner(): boolean {
    return this.isDataTab(this.activeTab) && this.isRightPanelLoading;
  }
}
